/**
 * Flattens a step's JSON output into addressable leaf paths so a test run and the original run can be
 * lined up row by row. Both sides come back from OrchestrationEngine in the same shape for exactly this
 * reason, which is what makes a path a fair basis for comparison.
 */

export interface OutputRow {
    /** Dotted path with array indices, e.g. `[0].validation.score`. */
    path: string;
    /** The leaf rendered for display. */
    value: string;
}

export interface OutputChange {
    path: string;
    /** Absent when the test added this path. */
    before?: string;
    /** Absent when the test removed this path. */
    after?: string;
}

/**
 * Agent-backed steps produce rich audit objects, but most of that data does not help someone judge a skill
 * edit. Keep these lists opt-in: an output-contract change should not accidentally expose a new identifier or
 * evidence payload as a meaningful comparison field.
 */
const MEANINGFUL_OUTPUT_PATHS_BY_STEP: Record<string, readonly RegExp[]> = {
    "analyze-one-node": [
        /^analyzedCohortCount$/,
        /^dimension\.(alias|reason)$/,
        /^dimension\.considered\[\d+\]$/,
        /^results\[\d+\]\.distribution\.(value|count|share)$/,
        /^results\[\d+\]\.(materialSource|reasoningModelName|undetermined|reviewedMaterialCount|reviewedMaterialCoverage)$/,
        /^results\[\d+\]\.temporalMetrics\[\d+\]\.(bucket|count|eddyCount|eddyRate)$/,
        /^results\[\d+\]\.results\[\d+\]\.(name|claim|reasoning|conversationCount|conversationShare|totalConversationsReviewed|analysisType)$/,
        /^selected\[\d+\]\.result\.(name|claim|reasoning|conversationCount|conversationShare|totalConversationsReviewed|analysisType)$/,
        /^selected\[\d+\]\.(exploreDeeper|reason)$/,
        /^visuals\[\d+\]\.(kind|title|caption)$/,
    ],
    "report-assembly": [
        /^\[\d+\]\.data\.(question|subject|previewText)$/,
        /^\[\d+\]\.data\.summary\.takeaway$/,
        /^\[\d+\]\.data\.summary\.scope\.(category|cohort|conversationsAnalyzed|totalConversationsInWindow|window)$/,
        /^\[\d+\]\.data\.summary\.scope\.triggeringSignal\.(name|kindLabel|volume)$/,
        /^\[\d+\]\.data\.summary\.scope\.triggeringSignal\.measurements\[\d+\]\.(label|kind|value|comparisonValue|comparisonLabel)$/,
        /^\[\d+\]\.data\.findings\[\d+\]\.(title|digest|whyItMatters|action|provenanceText|denominator|reviewedMaterialCount|strength|howIGotHere)$/,
        /^\[\d+\]\.data\.findings\[\d+\]\.visual\.(kind|displayLabel|reason|caption)$/,
        /^\[\d+\]\.email\.(subject|previewText|fromDisplayName|fromAddress|plainTextBody)$/,
    ],
};

export function hasFocusedOutputComparison(stepId: string): boolean {
    return MEANINGFUL_OUTPUT_PATHS_BY_STEP[stepId] !== undefined;
}

/** Uses a reviewed per-step allow-list when one exists; deterministic steps retain their complete output. */
export function meaningfulOutputRows(
    stepId: string,
    rows: OutputRow[],
): OutputRow[] {
    const includedPaths = MEANINGFUL_OUTPUT_PATHS_BY_STEP[stepId];
    if (!includedPaths) return rows;
    return rows.filter((row) =>
        includedPaths.some((includedPath) => includedPath.test(row.path)),
    );
}

/** Leaves are rendered, not re-serialized, so a string reads as text rather than a quoted blob. */
function renderLeaf(value: unknown): string {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "string") return value;
    return JSON.stringify(value);
}

function isBranch(value: unknown): value is object {
    return typeof value === "object" && value !== null;
}

/**
 * Walks `value` depth-first, emitting one row per leaf. An empty object or array is itself a leaf —
 * otherwise a field that went from populated to empty would vanish from the table instead of showing
 * as a change.
 */
function walk(value: unknown, prefix: string, rows: OutputRow[]): void {
    if (!isBranch(value)) {
        rows.push({ path: prefix, value: renderLeaf(value) });
        return;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            rows.push({ path: prefix, value: "[]" });
            return;
        }
        value.forEach((item, index) => walk(item, `${prefix}[${index}]`, rows));
        return;
    }

    const entries = Object.entries(value);
    if (entries.length === 0) {
        rows.push({ path: prefix, value: "{}" });
        return;
    }
    entries.forEach(([key, child]) =>
        walk(child, prefix ? `${prefix}.${key}` : key, rows),
    );
}

/** Parses and flattens a JSON payload. Unparseable text becomes a single row so it is still visible. */
export function flattenOutput(json: string | null | undefined): OutputRow[] {
    if (!json?.trim()) return [];
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return [{ path: "(unparsed)", value: json }];
    }
    const rows: OutputRow[] = [];
    walk(parsed, "", rows);
    return rows;
}

/**
 * Pairs values that differ between the two sides, including paths present on only one of them. Produced
 * paths retain their output order; removed paths follow in their original order.
 */
export function changedOutput(
    produced: OutputRow[],
    original: OutputRow[],
): OutputChange[] {
    const before = new Map(original.map((row) => [row.path, row.value]));
    const changes: OutputChange[] = [];

    for (const row of produced) {
        const originalValue = before.get(row.path);
        if (originalValue !== row.value) {
            changes.push({
                path: row.path,
                before: originalValue,
                after: row.value,
            });
        }
    }
    // A path the run used to produce and no longer does is a change too.
    const after = new Set(produced.map((row) => row.path));
    for (const row of original) {
        if (!after.has(row.path)) {
            changes.push({ path: row.path, before: row.value });
        }
    }

    return changes;
}
